import {useForm} from 'react-hook-form'
import {ChangeEvent, useEffect, useRef, useState} from 'react'

import {safeArray} from 'src/app/utils/array-utils'
import {MallModel, StoreCategoryModel, StoreModel} from 'src/app/modules/directory/models'
import {useGetMasterStore} from 'src/app/modules/directory/providers/Store.provider'
import {useGetMasterStoreCategory} from 'src/app/modules/directory/providers/StoreCategory.provider'
import {prizeListTypesSalesPromo} from 'src/app/components/Feedback/FilterMenu/FilterMenuContent.templates'
import {DialogRef} from 'src/app/components'
import {SalesPromoPrizeModel} from 'src/app/modules/campaign/models/SalesPromo.model'
import {useValidateBankRewards} from 'src/app/modules/rewards/providers/Rewards.provider'
import {
  useSalesPromoDetailPrizeItem,
  useSubmitSalesPromoPrize,
} from 'src/app/modules/campaign/providers/SalesPromo.provider'
import {SalesPromoAddPrizePopupProps} from './SalesPromoAddPrizePopup'

type REWARD_TYPES = 'DIGITAL' | 'FISIK' | 'PARKING' | 'VALET' | 'PRODUCT'
export interface SalesPromoPrizeFormModel extends SalesPromoPrizeModel {
  reward_type?: {label?: string; value?: REWARD_TYPES} // temporary, actual payload is type
  merchant_categories?: any[] // temporary, actual payload is merchant_category_ids
  merchants?: any[] // temporary, actual payload is merchant_ids
  directories?: MallModel[] // temporary, actual payload is directory_ids
  directory?: MallModel // temporary, actual payload is directory_ids with one item
  bank_rewards?: any[] // temporary, actual payload is bank_reward_codes
  bank_reward_codes_file?: any // temporary

  id?: string // intended for edit
  type?: REWARD_TYPES // reward type need set as ENUM values => DIGITAL, FISIK, PARKING, VALET
  name?: string
  directory_ids?: string[]
  merchant_category_ids?: string[]
  merchant_ids?: string[]
  is_bank_reward?: boolean
  bank_reward_codes?: any[] // mandatory if is_bank_reward = true
  description_idn?: string
  description_eng?: string
  image?: string
  quantity?: number
  days?: string[] //UPPERCASE
  validity_period_days?: number
  valid_until?: string
  discount_amount?: number
  reward_start_time?: string
  reward_end_time?: string
}

const dayMaster = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
export const DIGITAL = [
  'id',
  'type',
  'name',
  'directory_ids',
  'merchant_category_ids',
  'merchant_ids',
  'is_bank_reward',
  'bank_reward_codes',
  'description_idn',
  'description_eng',
  'image',
  'quantity',
  'days',
  'validity_period_days',
  'valid_until',
]

export const FISIK = [
  'id',
  'type',
  'name',
  'directory_ids',
  'merchant_category_ids',
  'merchant_ids',
  'description_idn',
  'description_eng',
  'image',
  'quantity',
]

export const PARKING = [
  'id',
  'type',
  'name',
  'directory_ids',
  'is_bank_reward',
  'bank_reward_codes',
  'description_idn',
  'description_eng',
  'image',
  'quantity',
  'days',
  'discount_amount',
  'reward_start_time',
  'reward_end_time',
  'validity_period_days',
  'valid_until',
]

export const VALET = PARKING

export const PRODUCT = [
  'id',
  'type',
  'name',
  'directory_ids',
  'description_idn',
  'description_eng',
  'image',
  'quantity',
]

const defaultForm: SalesPromoPrizeFormModel = {
  bank_reward_codes_file: undefined,
  is_bank_reward: undefined,
  id: undefined,
  description_eng: '',
  description_idn: '',
  draw_directory_id: '',
  reward_type: undefined,
  name: '',
  image: '',
  quantity: undefined,
  directory: undefined,
  directories: [],
  days: [],
  discount_amount: undefined,
  reward_start_time: '',
  reward_end_time: '',
  validity_period_days: undefined,
  valid_until: '',
  merchant_categories: [],
  merchants: [],
  bank_rewards: [],
  bank_reward_codes: [],
}

export function useSalesPromoAddPrizeForm(props: SalesPromoAddPrizePopupProps) {
  const validateBankRewards = useValidateBankRewards()
  const form = useForm({defaultValues: defaultForm, reValidateMode: 'onChange'})
  const [csvData, setCsvData] = useState<any[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [resultReader, setResultReader] = useState<any[]>([])
  const bankRewardDialogRef = useRef<DialogRef>(null)
  const bankRewardValidateDialogRef = useRef<DialogRef>(null)
  const [isLoadingParse, setIsLoadingParse] = useState(true) // Loading state for parsing
  const [isLoadingValidate, setIsLoadingValidate] = useState(false) // Loading state for validation

  const storeCategories = safeArray(form.watch('merchant_categories'))
  const days: string[] = safeArray(form.watch('days'))
  const directories = form.watch('directories')?.map((item) => item.id as string) || ['']
  const valPrizeType = form.watch('reward_type')?.value
  const id = form.watch('id')
  const valBankRewardCodes = safeArray(form.watch('bank_reward_codes'))
  const valBankRewards = safeArray(form.watch('bank_rewards'))
  const isVoucherFromTenant = form.watch('is_bank_reward')
  const stores = safeArray(form.watch('merchants'))
  const store = useGetMasterStore({
    directory_ids: directories,
    limit: 1000,
    page: 1,
    statuses: ['ACTIVE'],
  })

  const storeCategoryList = useGetMasterStoreCategory({
    limit: 1000,
    page: 1,
    statuses: ['ACTIVE'],
  })

  // reset merchant_categories, merchants while directories was changed
  const mallOnChange = () => {
    form.setValue('merchant_categories', [])
    form.setValue('merchants', [])
  }

  // reset mall and is_bank_reward if reward_type changed
  const rewardTypeOnChange = () => {
    form.setValue('directory', null as unknown as undefined)
    form.setValue('directories', [])
    form.setValue('is_bank_reward', false)
  }

  const submit = useSubmitSalesPromoPrize(props?.id as string)
  const detailPrizeItem = useSalesPromoDetailPrizeItem(
    props?.id,
    props.itemToEdit?.id,
    props.itemToEdit?.type,
    (data) => {
      const entries = Object.entries(data) as [keyof SalesPromoPrizeFormModel, any][]
      entries.forEach(([key, value]) => {
        form.setValue(key, value)
      })
      const reward_type = prizeTypes.find((i) => i.value.split('.').pop() === data.type)
      form.setValue('reward_type', reward_type)
      form.setValue('directory', data.directories?.[0])
    }
  )

  const [isEdit, setIsEdit] = useState(false)
  const [salesPromoDetailStatus, setSalesPromoDetailStatus] = useState('')

  useEffect(() => {
    if (props.itemToEdit) {
      if (props.itemToEdit?.id) {
        setIsEdit(true)
        setSalesPromoDetailStatus(props.itemToEdit?.status || '')
        detailPrizeItem.refetch()
      }
    }
  }, [props.itemToEdit])

  const enabledFields = () => {
    const fields = [
      'bank_reward_codes_file',
      'id',
      'name',
      'is_bank_reward',
      'description_idn',
      'description_eng',
      'draw_directory_id',
      'image',
      'quantity',
      'days',
      'validity_period_days',
      'valid_until',
      'reward_start_time',
      'reward_end_time',
    ]
    const fields2 = [
      'directories',
      'directory',
      'merchant_categories',
      'merchants',
      'discount_amount',
      ...fields,
    ]
    if (isEdit && salesPromoDetailStatus === 'ACTIVE') {
      return fields
    }
    if (isEdit) {
      return fields2
    }
    return ['reward_type', ...fields2]
  }

  const prizeTypes: {label: string; value: REWARD_TYPES}[] = prizeListTypesSalesPromo.map(
    (item) => {
      return {
        ...item,
        value: item.value.slice(1) as REWARD_TYPES,
      }
    }
  )

  useEffect(() => {
    if (valPrizeType === 'DIGITAL' && id) {
      const bankRewards = form.getValues().bank_rewards
      setCsvData(Array.isArray(bankRewards) ? bankRewards : [])
    }
  }, [form, id, valPrizeType])

  // sync merchant_category to merchant field
  const handleSyncStoreCategoryReward = (value: StoreCategoryModel | StoreCategoryModel[]) => {
    if (Array.isArray(value)) {
      const isNewValue = !storeCategories?.length || storeCategories?.length < value.length

      const stores = safeArray(form.getValues('merchants'))
      if (isNewValue) {
        const lastItem = value?.at(-1)
        const masterStores = store.data?.content?.filter(
          (_) => _.merchant_category_id === lastItem?.id
        )
        const mergedStores = safeArray(masterStores).reduce((stack, item) => {
          const isExist = stack.some((_) => _.id === item.id)
          return isExist ? stack : [...stack, item]
        }, stores)
        form.setValue('merchants', mergedStores)
      } else {
        const syncStores = stores.reduce<StoreModel[]>((stack, item) => {
          const isExists = value?.some((_) => _.id === item.merchant_category_id)
          return isExists ? [...stack, item] : stack
        }, [])
        form.setValue('merchants', syncStores)
      }
    }
  }

  // sync merchant to merchant_category field
  const handleSyncStore = (value: StoreModel | StoreModel[]) => {
    if (Array.isArray(value)) {
      const syncStoreCategories = value.reduce<StoreCategoryModel[]>((stack, item) => {
        const isExists = stack.some((_) => _.id === item.merchant_category_id)
        return isExists || !item.merchant_category ? stack : [...stack, item.merchant_category]
      }, [])
      form.setValue('merchant_categories', syncStoreCategories)
    }
  }

  // cycle everyday
  const handleChangeDay = (event: ChangeEvent<HTMLInputElement>) => {
    const _value = event.target.value
    if (event.target.checked) {
      form.setValue('days', [...days, _value], {shouldValidate: true})
    } else {
      const _days = days.filter((_) => _ !== _value)
      form.setValue('days', _days, {shouldValidate: true})
    }
  }

  const onChangeUploadBankRewardCodes = (event: any) => {
    bankRewardValidateDialogRef.current?.show()
    const base64String = event.base64.split(',')[1] // Remove the prefix
    const decodedData = atob(base64String) // Decode base64 to a string
    // Check if the file is a CSV format
    const fileName = event.filename.split('.').pop()
    if (fileName !== 'csv') {
      setErrors(['Wrong file format, please try again.'])
      form.setValue('bank_rewards', undefined)
      form.setValue('bank_reward_codes', undefined)
      form.setValue('quantity', undefined)
      setCsvData([])
      setIsLoadingParse(false)
      return
    }
    form.clearErrors('bank_reward_codes_file')
    form.resetField('quantity') //This is to reset error requrired validation state of quantity
    parseCSV(decodedData)
  }

  // Parse CSV
  const parseCSV = (text: string) => {
    setIsLoadingParse(true) // Start loading for parsing
    try {
      const lines = text.split('\n').map((line: string) => line.trim())
      const result = lines.map((line: string) => line.split(','))

      if (result.length > 0) {
        validateCSV(result)
      }
    } catch (error) {
      console.error('Error parsing CSV:', error)
    } finally {
      setTimeout(() => {
        setIsLoadingParse(false) // End loading for parsing
      }, 500)
    }
  }

  const validateCSV = (data: string[][]) => {
    const errorMessages: string[] = []

    // Remove the last index of the array if it is empty
    if (data[data.length - 1].length === 1 && data[data.length - 1][0] === '') {
      data.pop()
    }

    // Validation: Check header and add for only csv format
    if (data[0][0].trim() !== 'Voucher Code') {
      errorMessages.push('Wrong File Format, please try again.')
      return
    }

    // Validation: Check for empty columns
    const emptyRows: number[] = []
    data.forEach((row: string[], rowIndex: number) => {
      row.forEach((cell: string) => {
        if (cell.trim() === '') {
          emptyRows.push(rowIndex + 1) // +1 because rowIndex is 0-based, and A1 corresponds to row 1
          return // Exit the loop as we only need to flag the row once
        }
      })
    })

    if (emptyRows.length > 0) {
      const uniqueEmptyRows = Array.from(new Set(emptyRows)) // Remove duplicates
      errorMessages.push(
        `We found empty data in your file: row ${uniqueEmptyRows.map((i) => `A${i}`).join(', ')}`
      )
    }

    // Prepare existing codes for duplicate validation
    const existingCodes = new Set(form.getValues('bank_reward_codes')?.map((item) => item.code))

    // Validation: Check for duplicate rows within the new data and against existing data
    const rowMap = new Map<string, number[]>() // Map to store the string representation and corresponding row indices
    const newCodesSet = new Set<string>() // Track new codes in this upload to check for duplicates within this batch

    // Arrays to accumulate row numbers for the errors
    let existingDupes = []
    let newDupes = []

    // Skip the header row for validation
    for (let i = 1; i < data.length; i++) {
      const row = data[i]
      const code = row[0].trim()

      if (existingCodes.has(code)) {
        // Duplicate found with an existing code
        existingDupes.push(`A${i + 1}`)
      } else if (newCodesSet.has(code)) {
        // Duplicate found within the new data
        newDupes.push(`A${i + 1}`)
      } else {
        // Add code to the new set and map
        newCodesSet.add(code)

        if (!rowMap.has(code)) {
          rowMap.set(code, [])
        }
        rowMap.get(code)!.push(i + 1)
      }
    }

    // Create error messages based on the accumulated row numbers
    if (existingDupes.length > 0) {
      errorMessages.push(
        `We found data duplication with the existing data in database in your file: row ${existingDupes.join(
          ', '
        )}`
      )
    }

    if (newDupes.length > 0) {
      errorMessages.push(`We found data duplication in your file: row ${newDupes.join(', ')}`)
    }

    const result = data.slice(1).map((item: string[]) => ({
      code: item[0],
      status: 'Available',
    }))

    setResultReader(result)
    setErrors(errorMessages)

    if (errorMessages.length === 0) {
      setCsvData((prevCsvData) => [...prevCsvData, ...result])
      const currentBankRewardCodes = form.getValues('bank_reward_codes') || []
      const updatedBankRewardCodes = [...currentBankRewardCodes, ...result]
      form.setValue('bank_reward_codes', updatedBankRewardCodes)
      form.setValue('quantity', updatedBankRewardCodes.length)
    }
  }

  // Handle Validate Bank Reward Codes
  const handleValidateBankRewardCodes = async () => {
    setIsLoadingValidate(true) // Start loading for validation

    // only csvData.code value will be sent to the backend
    const result = resultReader.map((item: any) => item.code)

    try {
      const payloadValidate = {
        bank_reward_codes: result, // Replace with your actual values
        reward_id: detailPrizeItem.data?.id, // Replace with your actual reward ID
      }
      await validateBankRewards.mutateAsync(payloadValidate)
      bankRewardValidateDialogRef.current?.hide()
    } catch (error: any) {
      // insert setErrors here
      const errorMessages = error.response.data.response_output.errors.map(
        (item: any) => item.message.en
      )
      setErrors(errorMessages)
    } finally {
      setTimeout(() => {
        setIsLoadingValidate(false) // End loading for validation
      }, 500)
    }
  }

  return {
    action: {
      handleSyncStoreCategoryReward,
      handleSyncStore,
      handleChangeDay,
      onChangeUploadBankRewardCodes,
      handleValidateBankRewardCodes,
      rewardTypeOnChange,
      mallOnChange,
    },
    master: {days: dayMaster},
    state: {
      valPrizeType,
      valBankRewardCodes,
      valBankRewards,
      isVoucherFromTenant,
      days,
      directories,
      csvData,
      errors,
      bankRewardDialogRef,
      bankRewardValidateDialogRef,
      isLoadingParse,
      isLoadingValidate,
      storeCategories,
      stores,
    },
    storeCategoryList,
    store,
    form,
    submit,
    enabledFields,
    isEdit,
    setIsEdit,
    salesPromoDetailStatus,
    setSalesPromoDetailStatus,
    prizeTypes,
    detailPrizeItem,
    setCsvData
  }
}
